import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import IdChip from './IdChip';
import { Icons } from './Icons';
import { HelpPopover } from './shared';
import { createHexcolor } from '../../utils/colorsHandler';
import { epochToLocalDate } from '../../utils/dateConverter';

/**
 * Format any score-ish value to a fixed string. Numbers and numeric strings
 * are normalized; non-numeric strings (e.g. "A") pass through; null/undefined
 * fall back to an em-dash. Same logic PayloadNew used inline.
 */
const fmtScore = (v) => {
	const num = typeof v === 'number' ? v : Number(v);
	if (isNaN(num)) return v ?? '—';
	return num.toFixed(num % 1 === 0 ? 0 : 2);
};

/**
 * Derive the scoreGroups array from an `indicators` object (the same shape
 * returned under `.indicators` by both `/api/indicators/overview` and
 * `/api/indicators/results/overview`). Exported so callers can also use it for
 * rail-filter gating without rendering the card.
 */
export const deriveScoreGroups = (indicators) => {
	const details = indicators?.details;
	if (!Array.isArray(details)) return [];
	return details
		.map((g) => ({
			id: g?.group?._id?.$oid ?? g?.group?.name,
			name: g?.group?.description ?? g?.group?.name ?? '—',
			color: createHexcolor(g?.group?.color),
			kpis: (g?.indicators || []).filter((k) => k?.score !== undefined),
		}))
		.filter((g) => g.kpis.length > 0);
};

/**
 * Reusable "Payload Score" card. Renders the redesigned score block shared by
 * PayloadNew and SurveyResponse:
 *  - header with title + IdChip + date sub
 *  - big rounded total score on the right
 *  - per-group grid with color dot, group name and KPIs (with HelpPopover when
 *    the KPI carries a `message`).
 *
 * Returns `null` when there's nothing to render (no derived groups, or the
 * caller flagged a fetch error). Designed to live inside any wrapper that
 * exposes the design tokens — today that means `.payload-new`.
 *
 * @param {object} indicators        `{ details, score, _id, date }` — the
 *                                   `.indicators` payload returned by both
 *                                   indicators endpoints.
 * @param {boolean} [errored]        If true the card returns null regardless of
 *                                   the derived groups (lets callers hide stale
 *                                   data after a failed re-fetch).
 * @param {string}  [sectionId]      Optional id on the `<section>` for rail
 *                                   anchors (e.g. 'pn-score').
 * @param {string}  [className]      Extra class merged with the outer card.
 */
const PayloadScoreCard = ({ indicators, errored, sectionId, className }) => {
	const { t } = useTranslation();

	const scoreGroups = useMemo(() => deriveScoreGroups(indicators), [indicators]);
	const totalScore = indicators?.score;
	const indicatorsId = indicators?._id?.$oid;
	const indicatorsDate = indicators?.date?.$date
		? epochToLocalDate(indicators.date.$date, localStorage.getItem('i18nextLng'))
		: '';

	if (errored || scoreGroups.length === 0) return null;

	const totalText = fmtScore(totalScore);
	const totalHasDecimal = String(totalText).includes('.');

	return (
		<section
			id={sectionId}
			className={`pn-card ${className || ''}`.trim()}
		>
			<div className='pn-score-header'>
				<div>
					<div className='pn-score-title-row'>
						<h2 className='pn-section-title pn-score-title'>
							<Icons.award /> {t('payload.payloadScore.title')}
						</h2>
						{indicatorsId && <IdChip label='Id' value={indicatorsId} full />}
					</div>
					{indicatorsDate && (
						<div className='pn-score-sub'>{indicatorsDate}</div>
					)}
				</div>
				{totalScore != null && (
					<div className='pn-score-display'>
						<div className='pn-score-display-label'>{t('payload.payloadScore.score')}</div>
						<span className='pn-score-display-int'>
							{totalText.split('.')[0]}
							{totalHasDecimal && (
								<span className='pn-score-display-dec'>.{totalText.split('.')[1]}</span>
							)}
						</span>
					</div>
				)}
			</div>
			<div className='pn-score-grid'>
				{scoreGroups.map((g) => (
					<div
						className='pn-score-group'
						key={g.id}
						style={{ '--pn-group-color': g.color }}
					>
						<div className='pn-score-group-head'>
							<span className='pn-score-group-dot' style={{ background: g.color }} />
							<span className='pn-score-group-name'>{g.name}</span>
						</div>
						<div>
							{g.kpis.map((k, i) => (
								<div className='pn-score-kpi' key={i}>
									<div className='pn-score-kpi-name'>
										<span title={k?.description}>{k?.description ?? k?.name}</span>
										{k?.message && (
											<HelpPopover
												content={k.message}
												label={k?.description ?? k?.name}
											/>
										)}
									</div>
									<div className='pn-score-kpi-value' style={{ color: g.color }}>
										{fmtScore(k?.score)}
									</div>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</section>
	);
};

PayloadScoreCard.propTypes = {
	indicators: PropTypes.shape({
		details: PropTypes.array,
		score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		_id: PropTypes.shape({ $oid: PropTypes.string }),
		date: PropTypes.shape({ $date: PropTypes.number }),
	}),
	errored: PropTypes.bool,
	sectionId: PropTypes.string,
	className: PropTypes.string,
};

export default PayloadScoreCard;
